import React, { useCallback, useMemo, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { type TFunction } from 'i18next';

import {
  CreateFolderRequestBody,
  FolderType,
  getGetOrganizationItemsQueryKey,
  getGetOrganizationStructureQueryKey,
  GetOrganizationItemsParams,
  useCreateSubFolderForOrganization
} from '@/api/generated';
import SubFolderIcon from '@/assets/icons/hierarchy-chart-subfolder.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import { useSnackbar } from '@/hooks/useSnackbar';
import { borders } from '@/theme/constants';
import SettingsModalForm, { SettingsModalFormRef } from '@/views/SettingsModalForm/SettingsModalForm';

import CreateOrganizationItemModalContent from './CreateOrganizationItemModalContent';
import CreateOrganizationItemModalHeader from './CreateOrganizationItemModalHeader';
import { getCreateSubFolderBasicTabSchema } from './schema';
import SubFolderBasicInfoTab from './SubFolderBasicInfoTab';
import { CreateOrganizationItemFormData } from './types';

const getSteps = (t: TFunction) => [
  {
    content: SubFolderBasicInfoTab,
    title: t('layouts.organizations.create.tabs.basicInfo'),
    schema: getCreateSubFolderBasicTabSchema(t)
  }
];

interface CreateSubFolderModalProps {
  isOpen?: boolean;
  onClose: () => void;
  currentFilter?: GetOrganizationItemsParams | null;
  type?: FolderType;
  locationSlug?: string | null;
  organizationSlug?: string;
}

const CreateSubFolderModal: React.FC<CreateSubFolderModalProps> = ({
  isOpen = true,
  onClose,
  type,
  locationSlug,
  organizationSlug,
  currentFilter
}) => {
  const { t } = useTranslation(['organizations', 'common', 'validation']);
  const { showMessage } = useSnackbar();
  const formRef = useRef<SettingsModalFormRef<FieldValues>>(null);
  const queryClient = useQueryClient();

  const { mutateAsync: createSubFolder, isLoading: isFolderCreating } = useCreateSubFolderForOrganization();

  const isLoading = isFolderCreating;

  const handleFolderSave = useCallback(async () => {
    const values = formRef?.current?.getValues() as CreateOrganizationItemFormData;
    const data: CreateFolderRequestBody = {
      name: values?.name || '',
      legacyId: values?.legacyId
      // TODO: Add assignments once ready on backend
      // assignments: values?.assignments ?? []
    };

    try {
      data.parent = values?.parent;
      await createSubFolder({
        slug: organizationSlug!,
        data
      });
      await queryClient.invalidateQueries(getGetOrganizationItemsQueryKey(organizationSlug!, currentFilter!));
      await queryClient.invalidateQueries(getGetOrganizationStructureQueryKey(organizationSlug!));
    } catch (error) {
      showMessage(t('layouts.organizations.create.alerts.error'));
    }
  }, [createSubFolder, currentFilter, organizationSlug, queryClient, showMessage, t]);

  const { mapStructure } = useOrganizationStructure();

  const parent = locationSlug && mapStructure?.[locationSlug];

  const defaultValues = useMemo(
    () =>
      parent && {
        name: '',
        organization: organizationSlug,
        parent: {
          slug: parent.slug,
          type: parent.type
        }
      },
    [organizationSlug, parent]
  );

  const steps = useMemo(() => getSteps(t), [t]);

  const schema = useMemo(() => getCreateSubFolderBasicTabSchema(t), [t]);

  if (!defaultValues) return <BackdropLoader />;

  return (
    <SettingsModalForm
      ref={formRef}
      isOpen={isOpen}
      onClose={onClose}
      defaultValues={defaultValues}
      onSave={handleFolderSave}
      isSaving={isLoading}
      validation={schema}
      head={
        <CreateOrganizationItemModalHeader
          Icon={SubFolderIcon}
          title={t('layouts.organizations.create.items.subfolder')}
          organizationSlug={organizationSlug}
          locationSlug={locationSlug}
          type={type}
        />
      }
      sx={{
        '&.CreateOrganizationModal-root .SettingsModalForm-root': {
          maxWidth: 600,
          minHeight: 'auto',
          height: 'auto'
        },
        '&.CreateOrganizationModal-root .MuiDialogTitle-root': {
          py: 0,
          borderBottom: borders.default,
          borderColor: 'grey.300'
        }
      }}
      className="CreateOrganizationModal-root"
    >
      <CreateOrganizationItemModalContent
        draftExists={false}
        isLoading={isLoading}
        steps={steps}
        title={t('layouts.organizations.create.items.subfolder')}
      />
    </SettingsModalForm>
  );
};

export default CreateSubFolderModal;
